import { IServiceFactoryExtended } from '../service-factory-extended.interface';
import { IProgressiveWebAppService } from "./progressive-web-app.service.interface";
import { Check } from '@sparkware/app-utils';
import { observable, action, computed, makeObservable } from "mobx";
import { ServiceBase } from '../ServiceBase';
import { DomEventSubscription } from '../../../utils/event-listener-registration';
import {
    PWA_INSTALLED_CALLBACK, PWA_SHOULD_AUTO_POPUP_LOCAL_STORAGE_KEY,
    PWA_DISMISSED_POPUP_LOCAL_STORAGE_KEY,
    PWA_PROMPT_CALLBACK,
    PWA_PROMPT_EVENT,
} from "../../../common/const/service-worker-constants";
import { OrbitUserFlowAnalyticsCategory } from "../analytics/analytics.service.interface";


export class ProgressiveWebAppService extends ServiceBase implements IProgressiveWebAppService {

    @observable.ref
    private _onBeforeInstallEvent = null;

    private _clickSubscription: DomEventSubscription;

    @computed
    public get isPWAInstallable(): boolean {
        return Check.isFunction(this._onBeforeInstallEvent?.prompt);
    }

    constructor(services: IServiceFactoryExtended) {
        super(services);

        makeObservable(this);

        // disable if user chose not install previously
        if (this._didUserDismissedInstall()) {
            return;
        }

        const pwaEvent = this.services.window.getGlobalProperty(PWA_PROMPT_EVENT);
        if (pwaEvent) {
            // this means beforeinstallprompt handler was already called
            this._init(pwaEvent);
        }

        // beforeinstallprompt can arrive after service constructor finishes
        // and also can be called multiple times

        // register beforeinstallprompt callback
        this.services.window.setGlobalProperty(PWA_PROMPT_CALLBACK, (pwaEvent) => {
            this._init(pwaEvent);
        });

        // register appinstalled callback
        this.services.window.setGlobalProperty(PWA_INSTALLED_CALLBACK, () => {
            this._onAppInstalled();
        });

        this._subscribeReactions();

        this._subscribeToMainScreenClick();

        // add to analytics PWA launch event
        if ((this.services.device.isStandalone && this.services.device.browser.isSafari) || this.services.device.isProgressiveWebApp) {
            this.services.analytics.sendOrbitUserFlowEvent(OrbitUserFlowAnalyticsCategory.PWA, 'Launch', this.services.user.cid);
        }
    }


    private async _subscribeReactions(): Promise<void> {
        const cmsSegmentation = await this.services.cmsSegmentation;
        this.reaction(() => {
            return {
                isAuthorized: this.services.user.isAuthorized,
                isCstatus: cmsSegmentation.isCstatus,
                isUKOrIrl: cmsSegmentation.isUKOrIrl
            }
        }, data => {
            // If a user logged in (and has cstatus and isn't UK or Irl) he will mark as PWA installable client, which will later help do determine if installation popup should show 
            if (data.isAuthorized) {
                const shouldAutoPopup = data.isCstatus && !data.isUKOrIrl;
                shouldAutoPopup && this.services.localStorage.setItem(PWA_SHOULD_AUTO_POPUP_LOCAL_STORAGE_KEY, shouldAutoPopup);
            }
        },
            {
                fireImmediately: true
            }
        );
    }

    @action.bound
    // can be called multiple times by beforeinstallprompt handler
    private _init(onBeforeInstallEvent: any) {
        // disable it for hybrid and mini Orbit
        if (this.services.miniOrbit.isEnabled || this.services.device.hybrid.isHybrid) {
            return;
        }

        this._onBeforeInstallEvent = onBeforeInstallEvent;
    }

    private _subscribeToMainScreenClick() {
        this._clickSubscription = this.services.dom.subscribeTo.click(this._tryToShowPwaInstall);
    }

    private _unsubscribeFromMainScreenClick() {
        if (this._clickSubscription) {
            this._clickSubscription.dispose();
            this._clickSubscription = null;
        }
    }

    @action.bound
    private _onAppInstalled() {
        this.services.analytics.sendOrbitUserFlowEvent(OrbitUserFlowAnalyticsCategory.PWA, 'Browser_Prompt', 'INSTALL');
        this.services.logger.debug('PWA installed');
        this.services.window.setGlobalProperty(PWA_PROMPT_CALLBACK, null);
        this._onBeforeInstallEvent = null;
    }

    /* Auto PWA Installation prompt strategy:
    PWA is installable on client's device
    > Client has logged in at least once and he is now logged out (annonymous) 
    > When he was logged in it was with a user that has cStatus and isn't UK/IRL related 
    > Client hasn't dismissed pwa installation popup before
    */

    private async _shouldAutoPrompt(): Promise<boolean> {
        const cmsSegmentation = await this.services.cmsSegmentation;
        const sholdAutoPopup = this._shouldAutoPopupByStorage();
        const isDismissedPopup = this._didUserDismissedInstall(); // Check if user already got the PWA Installation prompt before and dismissed it
        const shouldPrompt = sholdAutoPopup && !isDismissedPopup && !cmsSegmentation.isAppBannerUKorIRL;
        return this.isPWAInstallable
            && shouldPrompt // PWA Installation prompt should be popped only for whos has logged in berfore at least one time
            && !this.services.user.isAuthorized; // PWA Installation prompt should be popped only for anonymous clients
    }

    private _tryToShowPwaInstall = async () => {
        if (await this._shouldAutoPrompt()) {
            this.prompt().catch(err => {
                this.services.logger.error('Attempt to show PWA prompt failed', err);
            });
        }
    }

    private _didUserDismissedInstall() {
        return this.services.localStorage.getItem(PWA_DISMISSED_POPUP_LOCAL_STORAGE_KEY) === 'true'
    }

    private _shouldAutoPopupByStorage() {
        return this.services.localStorage.getItem(PWA_SHOULD_AUTO_POPUP_LOCAL_STORAGE_KEY) === 'true'
    }

    prompt(): Promise<void> {
        if (!this.isPWAInstallable) {
            return Promise.reject("Not a PWA");
        }

        if (this._didUserDismissedInstall()) {
            return Promise.reject("PWA Install dismissed by client before");
        }

        try {
            return this._onBeforeInstallEvent.prompt()
                .then(res => {
                    this._unsubscribeFromMainScreenClick();
                    this.services.analytics.sendOrbitUserFlowEvent(OrbitUserFlowAnalyticsCategory.PWA, 'Browser_Prompt', 'SHOW');

                    // In case user dismissed the PWA installation prompt - store it so we won't pop it again
                    if (res?.outcome === 'dismissed') {
                        this.services.localStorage.setItem(PWA_DISMISSED_POPUP_LOCAL_STORAGE_KEY, 'true');
                        Promise.reject('PWA Install dismissed by client');
                    }

                    this.services.logger.debug(`PWA prompt() success with outcome: ${res && res.outcome}`);
                    return res;
                });
        }
        catch (err) {
            this.services.logger.error('Pwa exception caught', err);
            throw err;
        }
    }
}
